import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Text,
  Alert,
  Share,
  Platform,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { openExternalUrl } from '../../../src/utils/openExternalUrl';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import { RootState, AppDispatch } from '../../../src/redux/store';
import {
  Paprika,
  PaprikaSpacing,
  PaprikaTypography,
} from '../../../src/styles/theme';
import { Recipe } from '../../../src/types/recipe';
import { trackEvent } from '../../../src/services/analyticsService';
import unifiedRecipeService from '../../../src/services/unifiedRecipeService';
import { selectCurrentLanguage } from '../../../src/redux/languageSlice';
import { invalidateRecipeIndex, patchRecipeIndexEntry, removeRecipeIndexEntry } from '../../../src/redux/recipeIndexSlice';
import { addToWeek, removeFromWeek, markMealAsCooked, moveMeal, swapSlots, clearSlot, selectMealsForDateRange, selectPlannedMealsForRecipe, MealType, PlannedMeal, getCurrentMondayStart, getPersistableRecipeId, getWeekStartForDate } from '../../../src/redux/mealPlanSlice';
import { useWeekSnapshots } from '../../../src/hooks/useWeekSnapshots';
import { buildMovePlan, type MoveAction } from '../../../src/services/mealMovePlan';
import { selectActiveInventory, addItemsToPantryAndSync, removeItemAndSync } from '../../../src/redux/inventorySlice';
import { selectLearnedCoverage } from '../../../src/redux/pantrySelectors';
import { learnCoverage, savePantryLearning } from '../../../src/redux/pantryLearningSlice';
import { selectAllDeals } from '../../../src/redux/dealsSlice';
import { toggleFavorite } from '../../../src/redux/recipeMetadataSlice';
import { showChefToast, showGroceryToast } from '../../../src/redux/uiSlice';
import { matchIngredientsToDeals } from '../../../src/services/weekGeneratorService';
import { addIngredientsFromRecipe } from '../../../src/redux/groceryListSlice';
import { selectPreviousPath } from '../../../src/redux/navigationSlice';
import { resolveRecipeDetailBack } from '../../../src/utils/recipeDetailBack';
import type { IngredientInventoryMatch } from '../../../src/services/ingredientStatusService';
import { Toast, ToastType } from '../../../src/components/common/Toast';
import { isVideoUrl, hasRealDescription } from '../../../src/utils/recipeSource';
import { resolveRecipeStepIngredients } from '../../../src/utils/resolveStepIngredients';
import { toDisplayIngredients } from '../../../src/utils/recipeIngredientRows';
import { buildStepIngredients } from '../../../src/utils/stepIngredientAliases';
import { useStepChipOrphans } from '../../../src/hooks/useStepChipOrphans';
import { normalizeInstructions, coerceInstructionSections } from '../../../src/utils/normalizeInstructions';
import { buildDetailSteps } from '../../../src/utils/recipeDetailSteps';
import { useTranslation } from 'react-i18next';
import CookingModeModal from '../../../src/components/cooking/CookingModeModal';

// Import new Paprika-style components
import {
  RecipeHeroImage,
  RecipeHeader,
  RecipeActionsBar,
  CookLog,
  useCookLogSummary,
  type CookLogHandle,
  RecipeIngredients,
  RecipeCookingSteps,
  RecipeBottomBar,
  AddToMealPlanModal,
  type AddToMealPlanMode,
  type AddToMealPlanAction,
  type AddToMealPlanOptions,
  slotDishLabel,
  formatDishList,
  replaceSlotWithRecipe,
  RecipeEditMode,
  OverflowMenu,
  type MenuAnchor,
  type OverflowMenuItem,
  VideoEmbed,
  RecipeFetchSkeleton,
  RecipeDataErrorCard,
  type RecipeIngredient,
  type CookingStep,
  type CookingStepSection,
} from '../../../src/components/recipe-detail';
import { ConfirmationModal } from '../../../src/components/common/ConfirmationModal';
import { useRecipePlanStatus } from '../../../src/hooks/useRecipePlanStatus';
import { useRecipeAdjustment } from '../../../src/hooks/useRecipeAdjustment';
import { useRecipeDetailData } from '../../../src/hooks/useRecipeDetailData';
import { missingContentView } from '../../../src/hooks/recipeDetailState';
import { buildRecipeSharePayload, buildPaprikaRecipeUrl } from '../../../src/utils/buildRecipeSharePayload';
import { getSourceDisplayName } from '../../../src/utils/recipeSource';
import { cleanPantryName } from '../../../src/utils/pantryFoodName';
import { pantryDisplayName } from '../../../src/utils/pantryDisplayName';
import { isBlankRecipe } from '../../../src/utils/recipeContent';
import { recipeIndexEditChanges } from '../../../src/utils/recipeIndexEdit';
import { entryTitle, isEntryMeal } from '../../../src/redux/mealEntry';


/** Stable empty reference so the selector does not churn when there is no id. */
const EMPTY_PLANNED_MEALS: PlannedMeal[] = [];
const NO_EXCLUDED_STAPLES: string[] = [];

const RecipeDetailScreen: React.FC = () => {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const { snapshot, restore } = useWeekSnapshots();
  const { t, i18n } = useTranslation();
  const insets = useSafeAreaInsets();
  const { id, from, recipeData, plannedServings, slotDate, slotMealType, edit, draft } = useLocalSearchParams<{ id: string; from?: string; recipeData?: string; plannedServings?: string; slotDate?: string; slotMealType?: string; edit?: string; draft?: string }>();

  const { user } = useSelector((state: RootState) => state.auth);
  const inventoryItems = useSelector(selectActiveInventory);
  // Answers the user has already given to "does what you have cover this?".
  // Household-scoped like the rest of pantryLearning, so one member's answer
  // spares the other the same question.
  // Memoized (#668): built inline, this object was new on every dispatch and
  // re-rendered the whole screen.
  const learnedCoverage = useSelector(selectLearnedCoverage);
  // Basiques the cook turned off. The grocery list already lists them; without
  // them here this screen called the same line « basique » (#796).
  const excludedStaples = useSelector(
    (state: RootState) => state.userPreferences?.excludedStaples ?? NO_EXCLUDED_STAPLES
  );
  const householdId = useSelector((state: RootState) => state.auth?.householdId);
  const allDeals = useSelector(selectAllDeals);
  const currentWeekStart = useSelector(
    (state: RootState) => state.mealPlan?.currentWeekStart ?? getCurrentMondayStart()
  );
  const rating = useSelector((state: RootState) =>
    id ? state.recipeMetadata?.metadata[id]?.rating || 0 : 0
  );
  const timesPlanned = useSelector((state: RootState) =>
    id ? state.recipeMetadata?.metadata[id]?.timesPlanned || 0 : 0
  );
  const isFavorite = useSelector((state: RootState) =>
    id ? state.recipeMetadata?.metadata[id]?.isFavorite || false : false
  );
  const previousPath = useSelector(selectPreviousPath);
  const { width: windowWidth } = useWindowDimensions();
  const isWideLayout = windowWidth >= 768;
  // Drives the contextual primary action and the slot pill in the facts line.
  const planStatus = useRecipePlanStatus(id);
  // Every planned occurrence of this recipe — what move and remove act on, and
  // what lets undo put a meal back exactly where it came from.
  const plannedMealsForRecipe = useSelector((state: RootState) =>
    id ? selectPlannedMealsForRecipe(state, id) : EMPTY_PLANNED_MEALS
  );
  // The cook journal — the only thing that decides whether CookedLine shows.
  const cookSummary = useCookLogSummary(id ?? '');
  const currentLanguage = useSelector(selectCurrentLanguage);

  const [servings, setServings] = useState(4);
  const [baseServings, setBaseServings] = useState(4);
  const [showAddToMealPlan, setShowAddToMealPlan] = useState(false);
  const [planSheetMode, setPlanSheetMode] = useState<AddToMealPlanMode>('add');
  const [editMode, setEditMode] = useState(false); // the recipe editor, in place
  // `edit=true` opens the editor once per recipe, not again on every reload of it.
  const autoEditedRef = React.useRef<string | null>(null);
  // Set once a save or a delete settles the fate of a `draft=1` recipe.
  const draftSettledRef = React.useRef(false);
  const [cookingModalVisible, setCookingModalVisible] = useState(false);
  // Lets the ⋯ menu's "Noter une cuisson" open the CookLog capture sheet.
  const cookLogRef = React.useRef<CookLogHandle | null>(null);

  // ⋯ menu + its anchor, measured from whichever button opened it (the actions
  // bar on desktop, the hero on mobile).
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor | undefined>(undefined);
  // Delete confirmation. Uses ConfirmationModal rather than Alert.alert, which
  // react-native-web does not implement.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Toast state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<ToastType>('success');
  // Optional inline undo — how a move or a removal is taken back, instead of a
  // confirmation sheet in front of every reversible action.
  const [toastAction, setToastAction] = useState<{ label: string; onAction: () => void } | null>(null);

  const showToast = useCallback((
    message: string,
    type: ToastType = 'success',
    action?: { label: string; onAction: () => void },
  ) => {
    setToastMessage(message);
    setToastType(type);
    setToastAction(action ?? null);
    setToastVisible(true);
  }, []);

  // Loading, healing and translating the recipe lives in the hook — this screen
  // is one shared instance serving every recipe, and keeping the state machine
  // out here is what makes the stale-result guard testable (#565).
  const {
    recipe,
    editableRecipe,
    loading,
    error,
    contentStatus,
    scrapeStatus,
    scrapeErrorType,
    isTranslating,
    showOriginal,
    retry: handleRetry,
    applyLocalEdit,
    toggleShowOriginal,
  } = useRecipeDetailData({ id, from, recipeData, draft: draft === '1', onToast: showToast });
  const emptyView = missingContentView({ scrapeStatus, scrapeErrorType, contentStatus, sourceUrl: recipe?.sourceUrl });

  // The one adjustment state shared by the ingredients, the steps and the
  // cooking mode launched from this page. Declared after the recipe loads so it
  // can detect the system the recipe was actually written in.
  const adjustment = useRecipeAdjustment(recipe, servings, baseServings);
  // The recipe's Firestore document id — the only id that resolves. A recipe
  // that reaches this screen through the legacy loader (a direct link opened
  // before the household scope is known) carries a one-way hash on `id` and the
  // real `saved-…` id on `_firestoreId`. Every write and every key below goes
  // through this: saving an edit with the hash failed with "Recipe not found:
  // 496083684", and adding to the week stored a meal nothing could look up.
  const recipeKey = useMemo(() => (recipe ? getPersistableRecipeId(recipe) : ''), [recipe]);

  // Update servings when recipe loads
  useEffect(() => {
    if (recipe) {
      const recipeServings = typeof recipe.servings === 'string'
        ? parseInt(recipe.servings) || 4
        : recipe.servings || 4;
      setBaseServings(recipeServings);
      // If opened from a planned meal, use the planned servings
      const planned = plannedServings ? parseInt(plannedServings) : null;
      setServings(planned && planned > 0 ? planned : recipeServings);
    }
  }, [recipe, plannedServings]);

  // Show toast when recipe was just added
  useEffect(() => {
    if (from === 'add') {
      showToast(t('recipeDetail.savedToast'), 'success');
    }
  }, [from]);

  // Recipe state is reset by the hook; in-place edit mode belongs to the screen,
  // and must not survive into the next recipe. Declared before the `edit=true`
  // effect below, so on a first render that has the recipe already the reset
  // runs first and the auto-open wins.
  useEffect(() => {
    setEditMode(false);
  }, [id]);

  // A manually created recipe (`draft=1`) and the quality row's repair link
  // (`edit=true`) open the same editor as « Modifier » (#792).
  useEffect(() => {
    if (edit !== 'true' || !recipe || !id || autoEditedRef.current === id) return;
    autoEditedRef.current = id;
    setEditMode(true);
  }, [edit, recipe, id]);

  // Only offer "update from source" for non-video recipes — video URLs can't be scraped
  const canScrapeFromSource = !!(recipe?.sourceUrl && !recipe.videoUrl && !isVideoUrl(recipe.sourceUrl));

  // Navigation handlers
  const handleBack = useCallback(() => {
    // The rules live in resolveRecipeDetailBack; back used to land on My Meals.
    const back = resolveRecipeDetailBack({ from, canGoBack: router.canGoBack(), previousPath });
    if (back.kind === 'pop') {
      router.back();
    } else {
      router.replace(back.path as any);
    }
  }, [router, from, previousPath]);

  // Share handler — hands the OS its own sheet, nothing to build in Paprika.
  // The payload leads with the source URL (see buildRecipeSharePayload).
  const handleShare = useCallback(async () => {
    if (!recipe) return;

    try {
      const shareContent = buildRecipeSharePayload(
        {
          id: recipeKey,
          title: recipe.title,
          sourceUrl: recipe.sourceUrl,
          readyInMinutes: recipe.readyInMinutes,
        },
        {
          paprikaUrl: buildPaprikaRecipeUrl(recipeKey),
          labels: {
            minutes: (count) =>
              String(t('recipeDetail.durationMinutes', { defaultValue: '{{count}} min', count })),
            inPaprika: t('recipeDetail.shareInPaprika', 'Dans Du Chef :'),
          },
        }
      );

      if (Platform.OS === 'web') {
        if (navigator.share) {
          await navigator.share({
            title: shareContent.title,
            text: shareContent.message,
            url: shareContent.url,
          });
        } else {
          // No system sheet on desktop browsers — copy and confirm instead.
          await navigator.clipboard.writeText(shareContent.message);
          showToast(t('recipeDetail.linkCopied', 'Lien copié'), 'success');
        }
      } else {
        await Share.share({
          title: shareContent.title,
          message: shareContent.message,
          url: shareContent.url,
        });
      }
    } catch (error) {
      console.error('Error sharing recipe:', error);
    }
  }, [recipe, showToast, t]);

  const handleOpenMoreMenu = useCallback((anchor: MenuAnchor) => {
    setMenuAnchor(anchor);
    setMoreMenuOpen(true);
  }, []);

  // "Voir au plan" — jump to the day this recipe is scheduled on.
  const handleViewInPlan = useCallback(() => {
    if (!planStatus.plannedDate) return;
    router.push(`/(app)/day-detail/${planStatus.plannedDate}` as any);
  }, [router, planStatus.plannedDate]);

  const handleOpenSource = useCallback(async () => {
    if (!recipe?.sourceUrl) return;
    try {
      await openExternalUrl(recipe.sourceUrl);
    } catch (err) {
      console.error('Error opening source:', err);
    }
  }, [recipe?.sourceUrl]);

  // Add to meal plan handler
  const handleAddToPlan = useCallback(() => {
    setPlanSheetMode('add');
    setShowAddToMealPlan(true);
  }, []);

  // Same sheet, opened against the meal that is already on the plan — the
  // single entry point for "actually, let's eat this another day".
  const handleReschedule = useCallback(() => {
    setPlanSheetMode(planStatus.plannedMealId ? 'move' : 'add');
    setShowAddToMealPlan(true);
  }, [planStatus.plannedMealId]);

  // Edit recipe handler — opens the in-place edit mode
  const handleEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  // Favorite toggle
  const handleToggleFavorite = useCallback(() => {
    if (id) dispatch(toggleFavorite(id));
  }, [id, dispatch]);

  // Cook mode — cooking-plan flow when opened from a meal slot, standalone
  // CookingModeModal otherwise (routing to today's plan from an unplanned
  // recipe would start cooking a different meal).
  const handleCookMode = useCallback(() => {
    if (slotDate) {
      router.push(`/(app)/cooking-plan/${slotDate}`);
    } else {
      setCookingModalVisible(true);
    }
  }, [slotDate, router]);

  // Save edited recipe
  const handleSaveEdit = useCallback(async (updates: Partial<Recipe>) => {
    // A save attempt — even one that fails — means the cook meant to keep it.
    draftSettledRef.current = true;
    if (!recipe || !user?.uid) {
      showToast(t('recipeDetail.mustBeLoggedIn'), 'error');
      return false;
    }

    try {
      const success = await unifiedRecipeService.updateRecipe(
        user.uid,
        recipeKey,
        updates
      );

      if (success) {
        // Update local state with the changes
        applyLocalEdit(updates);

        // Invalidate the service cache AND the persisted recipe index so the
        // browser list reloads fresh data on next focus instead of showing
        // the pre-edit entry.
        unifiedRecipeService.invalidateCache();
        dispatch(invalidateRecipeIndex());
        // Patch the index entry in place too — invalidation alone still
        // renders the stale entry until the background refetch lands.
        dispatch(patchRecipeIndexEntry({
          id: recipeKey,
          changes: recipeIndexEditChanges(updates, String(editableRecipe?.title ?? recipe?.title ?? '')),
        }));

        showToast(t('editRecipe.saved'), 'success');
        return true;
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      showToast(t('editRecipe.saveFailed'), 'error');
      return false;
    }
  }, [recipe, user?.uid, showToast, dispatch, t]);

  // Delete recipe - throws on failure so modal can show error
  const handleDeleteRecipe = useCallback(async () => {
    // Validate upfront - throw if invalid
    if (!recipe) {
      throw new Error('No recipe to delete');
    }
    if (!user?.uid) {
      throw new Error('You must be logged in to delete recipes');
    }

    // Only the recipe document is awaited; the references it leaves behind are
    // swept after (#793). Throws on failure, and the cook stays on the recipe.
    draftSettledRef.current = true;
    const startedAt = Date.now();
    const { references } = await unifiedRecipeService.deleteRecipe(user.uid, recipeKey);
    const deleteMs = Date.now() - startedAt;

    // Gone from the list the cook may be sent back to, which draws a cached
    // copy for up to 30 minutes; then back where they came from, the same way
    // the arrow goes; and Du Chef says so there, since this page's own toast
    // unmounts with it.
    dispatch(removeRecipeIndexEntry(recipeKey));
    dispatch(showChefToast({
      message: t('recipeDetail.deleted', { title: recipe.title }),
      key: `recipe-deleted-${recipeKey}-${startedAt}`,
    }));
    handleBack();

    references.then((swept) => {
      trackEvent('recipe_deleted', {
        recipe_id: recipeKey,
        delete_ms: deleteMs,
        references_ms: Date.now() - startedAt - deleteMs,
        references_ok: swept !== null,
        planned_meals_removed: swept?.plannedMealsTouched ?? 0,
      });
    });
  }, [recipe, user?.uid, recipeKey, dispatch, t, handleBack]);

  // "Ajouter manuellement" writes a blank document so the editor has one to
  // save into, then opens it here with `draft=1`. Closing the editor before
  // anything was saved used to leave the blank behind — a "Nouvelle recette"
  // with no ingredients that the planners proposed as a dinner (#745). Only
  // that flow, and only while nothing was saved: a recipe opened for repair
  // (the quality row's edit=true) is never discarded, and neither is a
  // title-only save — that is the cook's choice.
  const handleCloseEditor = useCallback(() => {
    setEditMode(false);
    if (draft !== '1' || draftSettledRef.current || !recipe || !user?.uid) return;
    if (!isBlankRecipe(recipe)) return;
    draftSettledRef.current = true;
    unifiedRecipeService.deleteRecipe(user.uid, recipeKey)
      .then(() => {
        trackEvent('manual_recipe_draft_discarded', {});
        router.replace('/(app)/recipes');
      })
      .catch((err) => console.warn('[recipe-detail] could not discard the blank draft:', err?.message ?? err));
  }, [draft, recipe, user?.uid, router]);

  // "Mercredi soir" for a slot — the vocabulary the plan pill already uses.
  const formatSlotLabel = useCallback((date: string, mealType: MealType) => {
    const locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';
    let weekday = '';
    try {
      weekday = new Date(`${date}T12:00:00`).toLocaleDateString(locale, { weekday: 'long' });
    } catch {
      // unreported: an unknown locale tag only costs the weekday's wording.
      weekday = date;
    }
    return {
      date: weekday,
      mealType: t(`thisWeek.mealTypes.${mealType}`).toLowerCase(),
    };
  }, [i18n.language, t]);

  /**
   * Carry the planned meal to the slot the user picked, doing what they chose
   * for a slot that was already taken.
   *
   * Every branch goes through a week snapshot rather than an inverse dispatch:
   * a replace destroys the meal it evicted, and a cross-week swap rewrites two
   * buckets, so "move it back" is not an undo for either.
   */
  const handleMoveMeal = useCallback((
    mealId: string,
    date: string,
    mealType: MealType,
    action: MoveAction = 'move',
  ) => {
    const meal = plannedMealsForRecipe.find(m => m.id === mealId);
    if (!meal) return;

    const plan = buildMovePlan(meal, { scheduledFor: date, mealType }, action);
    if (!plan) return;

    const occupants = selectMealsForDateRange(store.getState(), date, date)
      .filter(m => m.mealType === mealType && m.id !== mealId);
    // A note or leftovers are the slot's main: name them, not the side beside them.
    const other = occupants.find(isEntryMeal) ?? occupants[0];
    const otherLabel = (other && isEntryMeal(other) ? entryTitle(other.entry!, () => undefined, t) : undefined)
      ?? other?.recipe?.title
      ?? other?.freeText
      ?? t('thisWeek.thisMeal', 'ce repas');
    const title = recipe?.title ?? t('thisWeek.thisMeal', 'ce repas');

    const undoKey = snapshot(plan.affectedWeeks);

    if (action === 'swap') {
      dispatch(swapSlots({ a: plan.source, b: plan.target }));
    } else {
      // Replace empties the slot first — the dish then lands in a free slot,
      // which is the one shape moveMeal never refuses.
      if (action === 'replace') dispatch(clearSlot(plan.target));
      dispatch(moveMeal({ mealId, newScheduledFor: date, newMealType: mealType }));
    }

    const message = action === 'swap'
      ? t('recipeDetail.swappedWithSlot', { title, other: otherLabel })
      : action === 'replace'
        ? t('recipeDetail.replacedAtSlot', { title, other: otherLabel })
        : action === 'stack'
          ? t('recipeDetail.stackedAtSlot', { title, other: otherLabel })
          : t('recipeDetail.movedToSlot', formatSlotLabel(date, mealType));

    showToast(message, 'success', {
      label: t('common.undo', 'Annuler'),
      onAction: () => restore(undoKey),
    });
  }, [plannedMealsForRecipe, dispatch, showToast, t, formatSlotLabel, recipe, snapshot, restore, store]);

  // Take the recipe off the plan. Undo re-plans the same slot; the meal gets a
  // fresh id, which is invisible for an upcoming meal that was never cooked.
  const handleRemoveFromPlan = useCallback((mealId: string) => {
    const meal = plannedMealsForRecipe.find(m => m.id === mealId);
    if (!meal) return;
    const snapshot = {
      recipe: meal.recipe,
      scheduledFor: meal.scheduledFor,
      mealType: meal.mealType,
      servings: meal.servings,
      createdAt: meal.createdAt,
    };

    dispatch(removeFromWeek(mealId));

    showToast(
      t('recipeDetail.removedFromPlan', 'Retiré du plan'),
      'success',
      {
        label: t('common.undo', 'Annuler'),
        onAction: () => {
          if (!snapshot.recipe) return;
          dispatch(addToWeek({
            recipe: snapshot.recipe,
            scheduledFor: snapshot.scheduledFor,
            mealType: snapshot.mealType,
            servings: snapshot.servings,
            createdAt: snapshot.createdAt,
          }));
        },
      },
    );
  }, [plannedMealsForRecipe, dispatch, showToast, t]);

  // Handle adding recipe to a specific slot
  const handleAddToSlot = useCallback((
    date: string,
    mealType: MealType,
    action: AddToMealPlanAction = 'add',
    fromMealId?: string,
    options?: AddToMealPlanOptions,
  ) => {
    if (!recipe?.id || !recipe?.title) return;

    // A move keeps the meal itself — its id, cook status, reviewed-ingredients
    // flag and creation stamp — instead of deleting and re-creating it. Moved
    // onto a day that is over with « Oui, je l'ai cuisinée », it is cooked
    // there (markMealAsCooked dates it on its new day).
    if (action === 'move' && fromMealId) {
      handleMoveMeal(fromMealId, date, mealType);
      if (options?.cooked) dispatch(markMealAsCooked({ mealId: fromMealId }));
      return;
    }

    try {
      const recipePayload = {
        id: recipeKey,
        title: recipe.title,
        image: recipe.image || '',
        readyInMinutes: recipe.readyInMinutes || 0,
        servings: servings,
        healthScore: recipe.healthScore,
        dishTypes: recipe.dishTypes,
        cuisines: recipe.cuisines,
        diets: recipe.diets,
        extendedIngredients: recipe.extendedIngredients?.map(ing => ({
          id: ing.id,
          name: ing.name,
          original: ing.original,
          amount: ing.amount,
          unit: ing.unit,
        })) || [],
        instructions: [],
        summary: recipe.summary || '',
      };

      // A replace empties the target slot — the main and the sides chosen for
      // it — then plants this recipe there, and says what left, with an undo
      // that brings those meals back as they were (#845).
      if (action === 'replace') {
        const undoKey = snapshot([getWeekStartForDate(date)]);
        const evicted = replaceSlotWithRecipe(dispatch, store.getState, {
          recipe: recipePayload,
          scheduledFor: date,
          mealType,
          servings,
          cooked: options?.cooked,
        });
        const other = formatDishList(
          evicted.map((m) => slotDishLabel(m, t)),
          i18n.language,
        ) || t('thisWeek.thisMeal', 'ce repas');
        showToast(t('recipeDetail.replacedAtSlot', { title: recipe.title, other }), 'success', {
          label: t('common.undo', 'Annuler'),
          onAction: () => restore(undoKey),
        });
        return;
      }

      dispatch(addToWeek({
        recipe: recipePayload,
        scheduledFor: date,
        mealType,
        servings: servings,
        cooked: options?.cooked,
      }));
    } catch (e) {
      console.warn('Failed to add recipe to plan:', e);
      showToast(t('recipeDetail.planFailed', 'Impossible d’ajouter au plan'), 'error');
      return;
    }

    showToast(t('recipeDetail.addedToSlot', formatSlotLabel(date, mealType)), 'success');
  }, [recipe, servings, dispatch, showToast, t, i18n.language, formatSlotLabel, handleMoveMeal, snapshot, restore, store]);

  // Servings change handler
  const handleServingsChange = useCallback((delta: number) => {
    setServings(prev => Math.max(1, Math.min(20, prev + delta)));
  }, []);

  // Restores the recipe's own servings — the servings half of "rétablir".
  const handleResetServings = useCallback(() => {
    setServings(baseServings);
  }, [baseServings]);

  // Cook button handler — opens cooking-plan flow when in a slot, or standalone CookingModeModal otherwise
  const handleCook = useCallback(() => {
    if (slotDate) {
      router.push(`/(app)/cooking-plan/${slotDate}`);
    } else {
      setCookingModalVisible(true);
    }
  }, [slotDate, router]);

  // Transform recipe ingredients to component format (before prep check so it's
  // available). Yield lines stored as ingredients are dropped here — see
  // `toDisplayIngredients` and issue #594.
  //
  // Memoized (#668): a fresh array here defeated the pantry-status memo in
  // RecipeIngredients on every render.
  const transformedIngredients: RecipeIngredient[] = useMemo(
    () => toDisplayIngredients(recipe?.extendedIngredients),
    [recipe?.extendedIngredients],
  );

  // Compute Lufa deal indices for ingredients.
  //
  // Indexed against `transformedIngredients`, NOT the raw stored list:
  // RecipeIngredients resolves a deal by an ingredient's position in the array
  // it was handed, so computing these over the unfiltered list would shift every
  // badge by one as soon as a yield line is dropped.
  const lufaDealIndices = React.useMemo(() => {
    const indices = new Set<number>();
    if (transformedIngredients.length > 0 && allDeals && allDeals.length > 0) {
      const ingredientStrings = transformedIngredients.map(ing => ing.original || ing.name || '');
      const matches = matchIngredientsToDeals(ingredientStrings, allDeals);
      for (const match of matches) {
        indices.add(match.ingredientIndex);
      }
    }
    return indices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipe?.extendedIngredients, allDeals]);

  // Pantry corrections — the only way to "fix" an ingredient status.
  // Removing/adding inventory recalculates the derived statuses on render,
  // and the grocery list follows via its auto-regeneration hook.
  const handleRemoveFromPantry = useCallback(
    (match: IngredientInventoryMatch) => {
      dispatch(removeItemAndSync({ itemId: match.inventoryItemId, userId: user?.uid }));
      // The pantry row that went, not the recipe line: with two tomato rows the
      // toast used to say the line was removed while the next row kept it checked.
      showToast(
        t('recipeDetail.removedFromPantryToast', {
          defaultValue: '{{name}} — retiré du garde-manger',
          name: pantryDisplayName({ name: match.inventoryItemName ?? '' }, i18n.language),
        }),
        'success'
      );
    },
    [dispatch, user?.uid, showToast, t, i18n.language]
  );

  /**
   * The user answered "you have X — does that cover Y?".
   *
   * Stored against the canonical id pair, not the row's display text, so the
   * lesson survives the recipe being re-scraped or translated. Persisted
   * immediately: a lesson lost to a reload teaches the user that answering the
   * app does not stick.
   */
  const handleAnswerCoverage = useCallback(
    ({ have, need, covers }: { have: string; need: string; covers: boolean }) => {
      dispatch(learnCoverage({ a: have, b: need, covers }));
      dispatch(savePantryLearning({ userId: user?.uid, householdId }) as any);
    },
    [dispatch, user?.uid, householdId]
  );

  const handleAddIngredientToPantry = useCallback(
    async (ingredientName: string) => {
      const result = await dispatch(
        addItemsToPantryAndSync({ ingredientNames: [ingredientName], userId: user?.uid }) as any
      );

      // The caller hands over the whole recipe line so the pantry and the
      // matcher reduce the same string (#601); the toast has to name what was
      // actually stored, not that line.
      const stored = cleanPantryName(ingredientName);
      const label = stored.usable ? stored.name : ingredientName;
      // The pantry drops names that collide with something already in it. Saying
      // "rangé au garde-manger" when nothing was written is how a user ends up
      // tapping the same button repeatedly and watching the cart never change.
      //
      // And say WHY. « déjà au garde-manger » was the answer to every refusal,
      // including a staple the pantry will not track — so the cook was told the
      // bay leaves were on the shelf while the row still said to buy them (#796).
      const payload = (result as any)?.payload as
        | { addedCount?: number; skipped?: Array<{ reason: string; existing?: string }> }
        | undefined;
      if (payload?.addedCount === 0) {
        const skip = payload.skipped?.[0];
        let message: string;
        if (skip?.reason === 'duplicate' && skip.existing && skip.existing.toLowerCase() !== label.toLowerCase()) {
          message = t('recipeDetail.alreadyInPantryAsToast', {
            defaultValue: '{{name}} — déjà au garde-manger : {{existing}}',
            name: label,
            existing: skip.existing,
          });
        } else if (skip?.reason === 'duplicate') {
          message = t('recipeDetail.alreadyInPantryToast', {
            defaultValue: '{{name}} — déjà au garde-manger',
            name: label,
          });
        } else if (skip?.reason === 'staple') {
          message = t('recipeDetail.stapleNotShelvedToast', {
            defaultValue: '{{name}} — basique, toujours compté comme en stock',
            name: label,
          });
        } else {
          message = t('recipeDetail.notShelvedToast', {
            defaultValue: '{{name}} — impossible à ranger au garde-manger',
            name: label,
          });
        }
        showToast(message, 'info');
        return;
      }
      showToast(
        t('recipeDetail.addedToPantryToast', {
          defaultValue: '{{name}} — rangé au garde-manger',
          name: label,
        }),
        'success'
      );
    },
    [dispatch, user?.uid, showToast, t, i18n.language]
  );

  const handleOpenPantry = useCallback(() => {
    router.push('/(app)/inventory');
  }, [router]);

  // Handler called when user clicks "Add Missing to Grocery" in prep mode
  const handleAddMissingToGrocery = useCallback((items: { name: string; original: string }[]) => {
    if (!recipe) return;

    try {
      // Filter out items with no usable name
      const validItems = items.filter(item => (item.name && item.name.trim()) || (item.original && item.original.trim()));
      if (validItems.length === 0) {
        showToast(t('recipeDetail.noItemsToAdd'), 'info');
        return;
      }

      // Transform items to include amount/unit from the full ingredient data
      const ingredientsWithAmounts = validItems.map(item => {
        // Match by original text first (most reliable), then by parsed name
        const fullIngredient = recipe.extendedIngredients?.find(
          ing => ing.original === item.original || ing.name === item.original || ing.name === item.name
        );
        return {
          name: item.name || item.original,
          original: item.original || item.name,
          amount: fullIngredient?.amount,
          unit: fullIngredient?.unit,
        };
      });

      const batchId = `grocery_${Date.now().toString(36)}`;
      dispatch(addIngredientsFromRecipe({
        recipeId: recipeKey,
        recipeName: recipe.title,
        servings: servings,
        ingredients: ingredientsWithAmounts,
        batchId,
      }));
      dispatch(showGroceryToast({ itemCount: validItems.length, addedIds: [], batchId }));
    } catch (e) {
      console.error('Failed to add items to grocery list:', e);
      showToast(t('recipeDetail.addToGroceryFailed'), 'error');
    }
  }, [recipe, servings, dispatch, showToast]);

  // Transform instructions to cooking steps format.
  // Scraped recipes often persist fragmented instructions (section titles stored
  // as steps, sentences split across entries). If the recipe already carries a
  // proper sectioned structure we trust it; otherwise we normalize the flat list
  // at render time (merge continuations, lift out section headers).
  // Chips are resolved for the whole recipe in one pass, not step by step: that
  // is what lets the matcher refuse a vague mention ("la sauce") on one step
  // while guaranteeing the ingredient still shows up on the step that names it.
  // Every text the branches below can render has to be in this list, so an
  // ingredient is never judged against a partial recipe.
  const normalizedForChips = normalizeInstructions(recipe?.instructions);
  // Sections are coerced before anything reads them: some recipes persist
  // `steps` as plain strings (#595), and useRecipeDetailData merges sections
  // straight from Firestore/local/shared without going through convertToRecipe,
  // so the read-path coercion alone does not cover this screen.
  const coercedSections = coerceInstructionSections(recipe?.instructionSections);
  const chipStepTexts = Array.from(
    new Set(
      [
        ...(recipe?.instructions ?? []).map(s => s.step || ''),
        ...coercedSections.flatMap(sec => sec.steps.map(s => s.step)),
        ...normalizedForChips.steps.map(s => s.step),
        ...normalizedForChips.sections.flatMap(sec => sec.steps.map(s => s.step)),
      ].filter(Boolean),
    ),
  );
  // Same preparation cooking mode uses, so a chip reads identically on both
  // screens: labels re-derived from the ingredient line, scavenged units dropped.
  const chipIngredients = recipe ? buildStepIngredients(recipe) : [];
  // An ingredient no step's chips will show is reported, never dropped in silence (#857).
  useStepChipOrphans({
    recipeId: recipeKey || undefined,
    language: i18n.language,
    stepTexts: chipStepTexts,
    ingredients: chipIngredients,
    stored: recipe?.stepIngredients,
    ingredientGroups: recipe?.ingredientGroups,
  });
  const chipsByStepText = new Map(
    resolveRecipeStepIngredients(
      chipStepTexts,
      chipIngredients,
      adjustment.options,
      recipe?.stepIngredients,
      recipe?.ingredientGroups,
    ).map((chips, i) => [chipStepTexts[i], chips]),
  );
  const enrichStep = (text: string) => chipsByStepText.get(text) ?? [];

  // Lives in src/utils so the precedence rule can be tested — jest's roots do
  // not cover app/. The rule that matters: never hand back an empty flat list
  // while the sections carry steps, because the gate below reads the flat list
  // alone (#678).
  const { steps: cookingSteps, sections: cookingStepSections } = buildDetailSteps(
    recipe,
    enrichStep,
    normalizedForChips,
  ) as { steps: CookingStep[]; sections?: CookingStepSection[] };

  // Extract author from recipe
  const getAuthor = (): string | undefined => {
    if (recipe?.sourceName && recipe.sourceName !== 'unknown') return recipe.sourceName;
    if (recipe?.creditsText) return recipe.creditsText;
    return undefined;
  };

  // Loading state
  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={Paprika.accent} />
        <Text style={styles.loadingText}>{t('recipeDetail.loading')}</Text>
      </View>
    );
  }

  // Error state
  if (error || !recipe) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.errorText}>
          {error === 'load-failed'
            ? t('recipeDetail.loadFailed')
            : t('recipeDetail.notFound')}
        </Text>
        <TouchableOpacity style={styles.backButton} onPress={handleBack}>
          <Text style={styles.backButtonText}>{t('common.back')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const sourceLabel = recipe.sourceUrl
    ? getSourceDisplayName(recipe.sourceUrl) || recipe.sourceName
    : undefined;

  // Everything the actions bar used to carry, minus the favorite (still on the
  // hero) and the two contextual buttons.
  const menuItems: OverflowMenuItem[] = [
    // The actions bar now spends its secondary slot on rescheduling, so jumping
    // to the day lives here — and mobile finally has the affordance at all.
    ...(planStatus.isPlanned && planStatus.plannedDate
      ? [{
          key: 'view-in-plan',
          label: t('recipeDetail.viewInPlan', 'Voir au plan'),
          onPress: handleViewInPlan,
        }]
      : []),
    {
      key: 'log-cook',
      label: t('recipeDetail.logCook', 'Noter une cuisson'),
      onPress: () => cookLogRef.current?.openCapture(),
    },
    {
      key: 'edit',
      label: t('recipeDetail.editRecipe', 'Modifier la recette'),
      onPress: handleEdit,
    },
    ...(recipe.sourceUrl
      ? [{
          key: 'open-source',
          label: t('recipeDetail.openOnSource', {
            defaultValue: 'Ouvrir sur {{source}}',
            source: sourceLabel || t('recipeDetail.sourceFallback', 'le site source'),
          }),
          onPress: handleOpenSource,
        }]
      : []),
    {
      key: 'delete',
      label: t('common.delete', 'Supprimer'),
      onPress: () => setDeleteConfirmOpen(true),
      destructive: true,
    },
  ];

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: PaprikaSpacing.xxl * 2 }}
      >
        {/* Hero — back, favorite, and (narrow only) share + ⋯ */}
        <RecipeHeroImage
          imageUrl={recipe.image || ''}
          onBack={handleBack}
          isFavorite={isFavorite}
          onToggleFavorite={handleToggleFavorite}
          onShare={handleShare}
          onMore={handleOpenMoreMenu}
        />

        {/* Recipe Header (title, source, facts, cook journal, description) */}
        <RecipeHeader
          title={recipe.title}
          sourceName={recipe.sourceName}
          sourceUrl={recipe.sourceUrl}
          description={hasRealDescription(recipe) ? recipe.summary : undefined}
          sourceRating={recipe.sourceRating}
          sourceRatingCount={recipe.sourceRatingCount}
          handsOnMinutes={recipe.handsOnMinutes}
          readyInMinutes={recipe.readyInMinutes}
          timeline={recipe.timeline}
          timelineStatus={recipe.timelineStatus}
          servings={servings}
          isPlanned={planStatus.isPlanned}
          slotLabel={planStatus.slotLabel}
          timesCooked={cookSummary.timesCooked}
          lastCooked={cookSummary.lastCooked}
          recipeId={id ?? ''}
          cookNotes={cookSummary.notes}
          isTranslating={isTranslating}
          isTranslated={recipe.isTranslated}
          originalLanguage={recipe.originalLanguage}
          showingOriginal={showOriginal}
          onToggleOriginal={toggleShowOriginal}
          actions={
            <RecipeActionsBar
              onCookMode={handleCookMode}
              onAddToPlan={handleAddToPlan}
              onShare={handleShare}
              onMore={handleOpenMoreMenu}
              isPlanned={planStatus.isPlanned}
              slotLabel={planStatus.slotLabel}
              onReschedule={planStatus.isPlanned ? handleReschedule : undefined}
            />
          }
        />

        {/* Video Embed for video-sourced recipes */}
        {recipe.videoUrl && recipe.videoPlatform ? (
          <VideoEmbed
            videoUrl={recipe.videoUrl}
            videoPlatform={recipe.videoPlatform}
            videoThumbnail={recipe.videoThumbnail}
          />
        ) : null}

        {/* Ingredients List */}
        {transformedIngredients.length > 0 ? (
          <RecipeIngredients
            ingredients={transformedIngredients}
            groups={recipe?.ingredientGroups}
            servings={servings}
            baseServings={baseServings}
            onServingsChange={handleServingsChange}
            yieldLabel={
              recipe?.servingsDisplay && /[a-zA-ZÀ-ÿ]{3,}/.test(recipe.servingsDisplay)
                ? recipe.servingsDisplay
                : undefined
            }
            onAddToGrocery={handleAddMissingToGrocery}
            inventory={inventoryItems}
            onRemoveFromPantry={handleRemoveFromPantry}
            onAddToPantry={handleAddIngredientToPantry}
            learnedCoverage={learnedCoverage}
            excludedStaples={excludedStaples}
            onAnswerCoverage={handleAnswerCoverage}
            onOpenPantry={handleOpenPantry}
            lufaDealIndices={lufaDealIndices}
            adjustment={adjustment}
            onResetServings={handleResetServings}
          />
        ) : emptyView === 'skeleton' ? (
          <RecipeFetchSkeleton section="ingredients" />
        ) : emptyView === 'scrape-error' && scrapeErrorType ? (
          <RecipeDataErrorCard
            errorType={scrapeErrorType}
            section="ingredients"
            onRetry={scrapeErrorType === 'network' && canScrapeFromSource ? handleRetry : undefined}
            sourceUrl={recipe.sourceUrl}
            onEditManually={handleEdit}
            returnTo={`/recipe-detail/${id}`}
          />
        ) : emptyView === 'unavailable' ? (
          // The load settled with nothing to show and no scrape ran (a video, or
          // an enrich failure). A recipe with no source has no page to blame and
          // gets the plain empty state below instead (missingContentView).
          <RecipeDataErrorCard
            errorType="not-found"
            section="ingredients"
            onRetry={handleRetry}
            sourceUrl={recipe.sourceUrl}
            onEditManually={handleEdit}
          />
        ) : (
          <View style={styles.emptySection}>
            <Feather name="list" size={20} color={Paprika.textMuted} />
            <Text style={styles.emptySectionText}>{t('recipeDetail.noIngredientsListed')}</Text>
            <TouchableOpacity style={styles.emptySectionButton} onPress={handleEdit}>
              <Feather name="edit-2" size={14} color={Paprika.accent} />
              <Text style={styles.emptySectionButtonText}>{t('recipeDetail.editRecipe')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Cooking Steps */}
        {cookingSteps.length > 0 ? (
          <RecipeCookingSteps
            steps={cookingSteps}
            sections={cookingStepSections}
            adjustment={adjustment}
            baseServings={baseServings}
            servings={servings}
            onResetServings={handleResetServings}
          />
        ) : emptyView === 'skeleton' ? (
          <RecipeFetchSkeleton section="steps" />
        ) : scrapeStatus === 'error' && scrapeErrorType === 'sign-in' && transformedIngredients.length === 0 ? (
          // One "create an account" is enough: the ingredients card above already asks.
          null
        ) : emptyView === 'scrape-error' && scrapeErrorType ? (
          <RecipeDataErrorCard
            errorType={scrapeErrorType}
            section="steps"
            onRetry={scrapeErrorType === 'network' && canScrapeFromSource ? handleRetry : undefined}
            sourceUrl={recipe.sourceUrl}
            onEditManually={handleEdit}
            returnTo={`/recipe-detail/${id}`}
          />
        ) : emptyView === 'unavailable' ? (
          <RecipeDataErrorCard
            errorType="not-found"
            section="steps"
            onRetry={handleRetry}
            sourceUrl={recipe.sourceUrl}
            onEditManually={handleEdit}
          />
        ) : (
          <View style={styles.emptySection}>
            <Feather name="file-text" size={20} color={Paprika.textMuted} />
            <Text style={styles.emptySectionText}>{t('recipeDetail.noInstructionsAdded')}</Text>
            <TouchableOpacity style={styles.emptySectionButton} onPress={handleEdit}>
              <Feather name="edit-2" size={14} color={Paprika.accent} />
              <Text style={styles.emptySectionButtonText}>{t('recipeDetail.editRecipe')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Cook notes + the capture sheet the ⋯ menu opens. Renders nothing
            until there are notes — no invite, no empty state. */}
        {recipeKey !== '' && <CookLog ref={cookLogRef} recipeId={recipeKey} />}
      </ScrollView>

      {/* Sticky bottom bar — the single contextual action on narrow layouts, and
          the servings + Cook bar when opened from a planned meal slot. */}
      {slotDate ? (
        <RecipeBottomBar
          servings={servings}
          servingsDisplay={recipe?.servingsDisplay}
          onServingsChange={handleServingsChange}
          onCook={handleCook}
        />
      ) : !isWideLayout ? (
        <View style={styles.mobileActionBar}>
          {planStatus.isPlanned ? (
            /* The plan status IS the button — tapping it opens the same sheet
               in move mode. Nothing about the plan is a dead end. */
            <View style={styles.mobileActionRow}>
              <TouchableOpacity
                style={styles.mobileActionOutline}
                onPress={handleReschedule}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel={t('recipeDetail.moveMealTitle', 'Déplacer ce repas')}
              >
                <Ionicons name="calendar" size={15} color={Paprika.textPrimary} />
                <Text style={styles.mobileActionOutlineText} numberOfLines={1}>
                  {planStatus.slotLabel ?? t('recipeDetail.viewInPlan', 'Voir au plan')}
                </Text>
                <Feather name="chevron-right" size={15} color={Paprika.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.mobileAction, styles.mobileActionFlex]}
                onPress={handleCook}
                activeOpacity={0.85}
              >
                <Text style={styles.mobileActionText}>{t('recipeDetail.cookMode', 'Mode cuisson')}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity style={styles.mobileAction} onPress={handleAddToPlan} activeOpacity={0.85}>
              <Text style={styles.mobileActionText}>{t('recipeDetail.plan', 'Planifier')}</Text>
              <Feather name="chevron-right" size={16} color="#fff" style={styles.mobileActionChevron} />
            </TouchableOpacity>
          )}
        </View>
      ) : null}

      {/* Fullscreen Cooking Mode Modal */}
      {recipe && (
        <CookingModeModal
          recipe={recipe}
          visible={cookingModalVisible}
          onClose={() => setCookingModalVisible(false)}
        />
      )}

      {/* Add to Meal Plan Modal */}
      <AddToMealPlanModal
        visible={showAddToMealPlan}
        recipeId={recipeKey}
        recipeTitle={recipe.title}
        onClose={() => setShowAddToMealPlan(false)}
        onAdd={handleAddToSlot}
        preselectedDate={slotDate}
        preselectedMealType={slotMealType as MealType | undefined}
        mode={planSheetMode}
        movingMealId={planStatus.plannedMealId}
        onMove={handleMoveMeal}
        onRemove={handleRemoveFromPlan}
      />

      {/* The recipe editor: « Modifier », « L'écrire » (draft=1) and the repair link (edit=true) */}
      {editMode && (
        <RecipeEditMode
          // The stored recipe, never the translation on screen: saving that
          // wrote the translated title and lines over the recipe's own (#792).
          recipe={editableRecipe ?? recipe}
          isNew={draft === '1'}
          onCancel={handleCloseEditor}
          onSave={async (updates) => {
            const saved = await handleSaveEdit(updates);
            if (saved) setEditMode(false);
            return saved;
          }}
        />
      )}

      {/* ⋯ menu */}
      <OverflowMenu
        visible={moreMenuOpen}
        onClose={() => setMoreMenuOpen(false)}
        items={menuItems}
        anchor={menuAnchor}
      />

      {/* Delete confirmation (reached from ⋯ → Supprimer) */}
      <ConfirmationModal
        visible={deleteConfirmOpen}
        title={t('recipeDetail.deleteTitle', 'Supprimer la recette')}
        message={t('recipeDetail.deleteConfirm', {
          defaultValue: 'Voulez-vous vraiment supprimer « {{title}} » ? Cette action est irréversible.',
          title: recipe.title,
        })}
        confirmLabel={t('common.delete', 'Supprimer')}
        cancelLabel={t('common.cancel', 'Annuler')}
        destructive
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          setDeleteConfirmOpen(false);
          handleDeleteRecipe().catch((err) => {
            showToast(t('editRecipe.deleteFailed'), 'error');
            console.error('Delete failed:', err);
          });
        }}
      />

      {/* Toast for feedback */}
      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        actionLabel={toastAction?.label}
        onAction={toastAction?.onAction}
        onDismiss={() => {
          setToastVisible(false);
          setToastAction(null);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  scrollView: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  /** Narrow layouts: one action, pinned to the bottom. */
  mobileActionBar: {
    borderTopWidth: 1,
    borderTopColor: Paprika.hairline,
    backgroundColor: Paprika.background,
    paddingTop: 10,
    paddingBottom: 18,
    paddingHorizontal: PaprikaSpacing.md,
  },
  mobileAction: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.sm,
    height: 48,
    borderRadius: 13,
    backgroundColor: Paprika.accent,
  },
  mobileActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  // Status button: sized to its label so "Mode cuisson" keeps the wider half
  // and stays the obvious primary action.
  mobileActionOutline: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 48,
    paddingHorizontal: PaprikaSpacing.md,
    borderRadius: 13,
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  mobileActionOutlineText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.textPrimary,
    flexShrink: 1,
  },
  mobileActionFlex: {
    flex: 1,
  },
  mobileActionText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: '#fff',
  },
  mobileActionChevron: {
    opacity: 0.55,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Paprika.background,
    padding: PaprikaSpacing.lg,
  },
  loadingText: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textSecondary,
    marginTop: PaprikaSpacing.md,
  },
  errorText: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  backButton: {
    marginTop: PaprikaSpacing.lg,
    paddingHorizontal: PaprikaSpacing.xl,
    paddingVertical: PaprikaSpacing.sm,
    borderRadius: 8,
    backgroundColor: Paprika.accent,
  },
  backButtonText: {
    ...PaprikaTypography.bodyMedium,
    color: '#fff',
  },
  emptySection: {
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.xl,
    paddingHorizontal: PaprikaSpacing.lg,
    gap: PaprikaSpacing.sm,
  },
  emptySectionText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textMuted,
  },
  emptySectionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.xs,
    marginTop: PaprikaSpacing.xs,
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    borderWidth: 1,
    borderColor: Paprika.accent,
    borderRadius: 20,
  },
  emptySectionButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.xs,
    marginHorizontal: PaprikaSpacing.lg,
    marginTop: PaprikaSpacing.xl,
    paddingVertical: PaprikaSpacing.md,
  },
  deleteButtonText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.destructive,
  },
});

export default RecipeDetailScreen;
